import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { sculptures, heroPortrait } from "../data/sculptures";
import { ArrowUpRight, X, Plus, ChevronLeft, ChevronRight } from "lucide-react";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: i * 0.06 },
    }),
};

const Nav = () => {
    const [time, setTime] = useState("");
    useEffect(() => {
        const update = () => {
            const d = new Date();
            const t = d.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
            });
            setTime(t);
        };
        update();
        const id = setInterval(update, 30000);
        return () => clearInterval(id);
    }, []);

    return (
        <header
            className="fixed top-0 left-0 right-0 z-50 px-6 sm:px-10 lg:px-16 py-5 flex items-center justify-between mix-blend-difference text-white"
            data-testid="site-header"
        >
            <a
                href="#top"
                className="font-serif-display text-lg tracking-tight"
                data-testid="brand-link"
            >
                Adrian Berndt
            </a>
            <nav className="hidden md:flex items-center gap-10 text-[11px] uppercase tracking-[0.22em] font-medium">
                <a href="#work" className="link-reveal" data-testid="nav-work">
                    Work
                </a>
                <a href="#about" className="link-reveal" data-testid="nav-about">
                    About
                </a>
                <a
                    href="#contact"
                    className="link-reveal"
                    data-testid="nav-contact"
                >
                    Contact
                </a>
            </nav>
            <span className="text-[11px] uppercase tracking-[0.22em] font-medium tabular-nums">
                {time}
            </span>
        </header>
    );
};

const Hero = () => {
    return (
        <section
            id="top"
            className="relative min-h-screen w-full px-6 sm:px-10 lg:px-16 pt-32 pb-16"
            data-testid="hero-section"
        >
            <div className="grid grid-cols-12 gap-6 lg:gap-10 min-h-[calc(100vh-12rem)] items-end">
                {/* Left meta column */}
                <div className="col-span-12 md:col-span-3 flex md:flex-col justify-between md:justify-end gap-8 text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium">
                    <div className="flex flex-col gap-1">
                        <span>Index</span>
                        <span className="text-stone-900">01 — Sculptures</span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span>Selected works</span>
                        <span className="text-stone-900">Est. 2026</span>
                    </div>
                </div>

                {/* Center title */}
                <div className="col-span-12 md:col-span-6">
                    <motion.h1
                        initial="hidden"
                        animate="visible"
                        custom={1}
                        variants={fadeUp}
                        className="font-serif-display font-light leading-[0.92] tracking-tight text-[18vw] md:text-[11vw] lg:text-[9.6vw] text-stone-950"
                    >
                        Adrian
                        <br />
                        <span className="italic font-light text-stone-700">
                            Berndt
                        </span>
                        <span className="text-stone-900">.</span>
                    </motion.h1>

                    <motion.p
                        initial="hidden"
                        animate="visible"
                        custom={3}
                        variants={fadeUp}
                        className="mt-10 max-w-md text-base sm:text-lg leading-relaxed text-stone-600"
                    >
                        Just a person, sculpting with paperclips and the
                        things at hand.
                    </motion.p>
                </div>

                {/* Right portrait */}
                <motion.div
                    initial="hidden"
                    animate="visible"
                    custom={2}
                    variants={fadeUp}
                    className="col-span-12 md:col-span-3 self-end"
                >
                    <div className="media-hover aspect-[3/4] w-full overflow-hidden bg-stone-100">
                        <img
                            src={heroPortrait}
                            alt="Portrait of Adrian Berndt"
                            className="w-full h-full object-cover"
                            data-testid="hero-portrait"
                        />
                    </div>
                    <div className="mt-3 flex items-baseline justify-between text-[11px] uppercase tracking-[0.22em] text-stone-500">
                        <span>Fig. 01</span>
                        <span>The artist</span>
                    </div>
                </motion.div>
            </div>

            {/* Scroll cue */}
            <div className="mt-16 flex items-center justify-between border-t border-stone-200 pt-6">
                <span className="text-[11px] uppercase tracking-[0.22em] text-stone-500">
                    Scroll
                </span>
                <span className="text-[11px] uppercase tracking-[0.22em] text-stone-500">
                    One work — Many views
                </span>
            </div>
        </section>
    );
};

const Marquee = () => (
    <section
        aria-hidden="true"
        className="overflow-hidden border-y border-stone-200 py-8"
    >
        <div className="marquee-track flex whitespace-nowrap will-change-transform">
            {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex items-center gap-12 pr-12">
                    {[
                        "Sculpture",
                        "Paperclips",
                        "Eraser",
                        "Neuron",
                        "Chaos",
                        "Order",
                        "Motion",
                        "Thought",
                        "Form",
                        "Creature",
                    ].map((w, j) => (
                        <span
                            key={j}
                            className="font-serif-display italic text-5xl md:text-6xl font-light text-stone-900 flex items-center gap-12"
                        >
                            {w}
                            <span className="text-stone-300">·</span>
                        </span>
                    ))}
                </div>
            ))}
        </div>
    </section>
);

const About = () => (
    <section
        id="about"
        className="relative px-6 sm:px-10 lg:px-16 py-24 sm:py-32 border-b border-stone-200"
        data-testid="about-section"
    >
        <div className="grid grid-cols-12 gap-6 lg:gap-10">
            <div className="col-span-12 md:col-span-3">
                <p className="text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium">
                    (About)
                </p>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
                className="col-span-12 md:col-span-9"
            >
                <h2 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-stone-950 max-w-4xl">
                    I make physical sculptures to show artwork to people.
                </h2>

                <div className="mt-12 max-w-4xl text-stone-600 leading-relaxed">
                    <p>
                        I'm Adrian. I'm 15. I wanted to make sculptures.
                        One afternoon I was messing with paperclips in class
                        and ended up making a creature. After the creature
                        in class, I personally found that process fun.
                        That's why I decided to make more.
                    </p>
                </div>

                <dl className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl border-t border-stone-200 pt-8">
                    {[
                        ["Born", "22 April 2011"],
                        ["Studio", "Pending"],
                        ["Materials", "Paperclips · Eraser"],
                    ].map(([k, v]) => (
                        <div key={k}>
                            <dt className="text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium">
                                {k}
                            </dt>
                            <dd className="mt-2 text-stone-900 font-sans-ui text-sm">
                                {v}
                            </dd>
                        </div>
                    ))}
                </dl>
            </motion.div>
        </div>
    </section>
);

const GalleryItem = ({ piece, index, onOpen }) => {
    const primary = piece.images[0];
    const thumbs = piece.images.slice(1);

    return (
        <motion.figure
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{
                duration: 1,
                delay: (index % 2) * 0.08,
                ease: [0.19, 1, 0.22, 1],
            }}
            className="col-span-12"
            data-testid={`gallery-item-${piece.id}`}
        >
            <div className="grid grid-cols-12 gap-6 lg:gap-10">
                <button
                    onClick={() => onOpen(piece, 0)}
                    className="group block col-span-12 md:col-span-8 text-left"
                    data-testid={`gallery-open-${piece.id}`}
                    aria-label={`Open ${piece.title} gallery`}
                >
                    <div className="media-hover aspect-[3/4] w-full overflow-hidden bg-stone-100 relative flex items-center justify-center">
                        <img
                            src={primary.url}
                            alt={`${piece.title} — ${primary.view}`}
                            className={`w-full h-full ${piece.fit === "cover" ? "object-cover" : "object-contain"}`}
                            loading="lazy"
                        />
                        <div className="absolute top-4 right-4 px-3 h-9 rounded-full bg-white/0 group-hover:bg-white/90 backdrop-blur-sm flex items-center gap-2 transition-all duration-500 opacity-0 group-hover:opacity-100">
                            <Plus className="w-4 h-4 text-stone-900" />
                            <span className="text-[11px] uppercase tracking-[0.15em] font-medium text-stone-900">
                                {piece.images.length} views
                            </span>
                        </div>
                    </div>
                </button>

                <div className="col-span-12 md:col-span-4 flex flex-col">
                    <div className="grid grid-cols-2 gap-3">
                        {thumbs.map((img, i) => (
                            <button
                                key={i}
                                onClick={() => onOpen(piece, i + 1)}
                                className="media-hover aspect-square overflow-hidden bg-stone-100"
                                data-testid={`gallery-thumb-${piece.id}-${i + 1}`}
                                aria-label={`Open ${piece.title} — ${img.view}`}
                            >
                                <img
                                    src={img.url}
                                    alt={`${piece.title} — ${img.view}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </button>
                        ))}
                    </div>

                    <figcaption className="mt-6 border-t border-stone-200 pt-4 flex-1">
                        <div className="flex items-baseline justify-between gap-6">
                            <h3 className="font-serif-display font-light text-2xl sm:text-3xl text-stone-950 leading-tight">
                                {piece.title}
                            </h3>
                        </div>
                        <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-stone-500 font-medium">
                            {piece.material}
                        </p>
                        <p className="mt-4 text-sm text-stone-600 leading-relaxed">
                            {piece.description}
                        </p>
                    </figcaption>
                </div>
            </div>
        </motion.figure>
    );
};

const Lightbox = ({ piece, startIndex = 0, onClose }) => {
    const [active, setActive] = useState(startIndex);
    const total = piece.images.length;
    const current = piece.images[active];

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight")
                setActive((i) => (i + 1) % total);
            if (e.key === "ArrowLeft")
                setActive((i) => (i - 1 + total) % total);
        };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose, total]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
            className="fixed inset-0 z-[80] bg-[#f9f9f8]"
            data-testid="lightbox"
        >
            <button
                onClick={onClose}
                className="absolute top-5 right-5 sm:top-8 sm:right-8 z-10 w-11 h-11 border border-stone-300 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-all duration-500 flex items-center justify-center"
                aria-label="Close"
                data-testid="lightbox-close"
            >
                <X className="w-4 h-4" />
            </button>

            <div className="h-full w-full overflow-y-auto px-6 sm:px-10 lg:px-16 py-20">
                <div className="grid grid-cols-12 gap-6 lg:gap-10 min-h-full items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="col-span-12 md:col-span-7"
                    >
                        <div className="relative w-full bg-stone-100">
                            <img
                                key={current.url}
                                src={current.url}
                                alt={`${piece.title} — ${current.view}`}
                                className="w-full h-auto object-contain max-h-[78vh]"
                            />
                            {total > 1 && (
                                <>
                                    <button
                                        onClick={() =>
                                            setActive(
                                                (i) => (i - 1 + total) % total,
                                            )
                                        }
                                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm hover:bg-stone-900 hover:text-white text-stone-900 transition-all duration-300 flex items-center justify-center"
                                        aria-label="Previous view"
                                        data-testid="lightbox-prev"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setActive((i) => (i + 1) % total)
                                        }
                                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm hover:bg-stone-900 hover:text-white text-stone-900 transition-all duration-300 flex items-center justify-center"
                                        aria-label="Next view"
                                        data-testid="lightbox-next"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </>
                            )}
                        </div>

                        {total > 1 && (
                            <div className="mt-4 flex items-center gap-3 overflow-x-auto pb-2">
                                {piece.images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActive(i)}
                                        className={`shrink-0 w-20 h-20 overflow-hidden bg-stone-100 transition-all duration-300 ${
                                            i === active
                                                ? "ring-1 ring-stone-900 opacity-100"
                                                : "opacity-50 hover:opacity-100"
                                        }`}
                                        data-testid={`lightbox-thumb-${i}`}
                                        aria-label={`View ${img.view}`}
                                    >
                                        <img
                                            src={img.url}
                                            alt={img.view}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="col-span-12 md:col-span-5 md:pl-8 md:sticky md:top-20"
                    >
                        <h2 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1] text-stone-950">
                            {piece.title}
                        </h2>
                        {piece.longDescription ? (
                            <div className="mt-8 space-y-4 text-base sm:text-lg leading-relaxed text-stone-700 max-w-md">
                                {piece.longDescription
                                    .split(/\n\n+/)
                                    .map((para, i) => (
                                        <p
                                            key={i}
                                            className={
                                                i === 0
                                                    ? "font-serif-display italic text-stone-950 text-xl sm:text-2xl leading-snug whitespace-pre-line"
                                                    : ""
                                            }
                                        >
                                            {para}
                                        </p>
                                    ))}
                            </div>
                        ) : (
                            <p className="mt-8 text-base sm:text-lg leading-relaxed text-stone-700 max-w-md">
                                {piece.description}
                            </p>
                        )}
                        <dl className="mt-10 border-t border-stone-200 pt-6 grid grid-cols-2 gap-6 max-w-md">
                            <div>
                                <dt className="text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium">
                                    Material
                                </dt>
                                <dd className="mt-2 text-stone-900 text-sm">
                                    {piece.material}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium">
                                    Dimensions
                                </dt>
                                <dd className="mt-2 text-stone-900 text-sm">
                                    {piece.size}
                                </dd>
                            </div>
                        </dl>
                        <a
                            href="#contact"
                            onClick={onClose}
                            className="mt-12 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] font-medium text-stone-900 link-reveal"
                            data-testid="lightbox-inquire"
                        >
                            Inquire about this piece
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
};

const Gallery = ({ onOpen }) => (
    <section
        id="work"
        className="px-6 sm:px-10 lg:px-16 py-24 sm:py-32"
        data-testid="gallery-section"
    >
        <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-16">
            <div className="col-span-12 md:col-span-3">
                <p className="text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium">
                    (Selected works)
                </p>
            </div>
            <div className="col-span-12 md:col-span-9">
                <h2 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-stone-950 max-w-3xl">
                    Five creatures.
                </h2>
            </div>
        </div>

        <div className="grid grid-cols-12 gap-6 lg:gap-10">
            {sculptures.map((s, i) => (
                <GalleryItem key={s.id} piece={s} index={i} onOpen={onOpen} />
            ))}
        </div>
    </section>
);

const Contact = () => (
    <section
        id="contact"
        className="relative px-6 sm:px-10 lg:px-16 py-24 sm:py-32 border-t border-stone-200"
        data-testid="contact-section"
    >
        <div className="grid grid-cols-12 gap-6 lg:gap-10">
            <div className="col-span-12 md:col-span-3">
                <p className="text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium">
                    (Contact)
                </p>
            </div>
            <div className="col-span-12 md:col-span-9">
                <h2 className="font-serif-display font-light text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-stone-950">
                    Email <span className="italic">me</span> if you want, you
                    don't have to.{" "}
                    <span
                        className="whitespace-nowrap align-middle"
                        style={{
                            fontFamily:
                                '"DejaVu Sans", "Segoe UI Symbol", "Noto Sans Symbols2", "Apple Symbols", sans-serif',
                            fontSize: "0.58em",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {">⩊<"}
                    </span>
                </h2>
                <p className="mt-10 max-w-xl text-stone-600 leading-relaxed text-base sm:text-lg">
                    For exhibitions or just to say
                    hello — write to me directly. I read almost everything.
                </p>

                <a
                    href="mailto:adrian.berndt01@gmail.com?subject=Hello%20Adrian"
                    className="group mt-12 inline-flex items-end gap-4 border-b border-stone-300 pb-4 hover:border-stone-900 transition-colors duration-500"
                    data-testid="contact-email-link"
                >
                    <span className="font-serif-display italic text-3xl sm:text-5xl lg:text-6xl text-stone-950 font-light leading-none">
                        adrian.berndt01@gmail.com
                    </span>
                    <ArrowUpRight className="w-6 h-6 sm:w-8 sm:h-8 text-stone-900 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" />
                </a>

                <div className="mt-16 grid grid-cols-2 gap-8 max-w-2xl border-t border-stone-200 pt-8">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium">
                            Studio
                        </p>
                        <p className="mt-2 text-sm text-stone-900">
                            Pending
                        </p>
                    </div>
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium">
                            Response time
                        </p>
                        <p className="mt-2 text-sm text-stone-900">
                            Typically within the day{" "}
                            <span className="font-mono font-bold text-stone-950 tracking-tight ml-1">:3</span>{" "}
                            <span className="font-mono font-bold text-stone-950 tracking-tight">{'<3'}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const Footer = () => (
    <footer
        className="px-6 sm:px-10 lg:px-16 py-10 border-t border-stone-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        data-testid="site-footer"
    >
        <p className="font-serif-display italic text-stone-600 text-lg">
            © {new Date().getFullYear()} Adrian Berndt — All works in progress.
        </p>
        <div className="flex items-center gap-8 text-[11px] uppercase tracking-[0.22em] text-stone-500 font-medium">
            <a href="#top" className="link-reveal" data-testid="footer-top">
                Back to top
            </a>
            <span>Made by hand</span>
        </div>
    </footer>
);

export default function Portfolio() {
    const [openPiece, setOpenPiece] = useState(null);
    const [startIndex, setStartIndex] = useState(0);

    const handleOpen = (piece, index = 0) => {
        setStartIndex(index);
        setOpenPiece(piece);
    };

    return (
        <div className="grain min-h-screen bg-[#f9f9f8] text-stone-950">
            <Nav />
            <main>
                <Hero />
                <About />
                <Gallery onOpen={handleOpen} />
                <Contact />
            </main>
            <Footer />

            <AnimatePresence>
                {openPiece && (
                    <Lightbox
                        piece={openPiece}
                        startIndex={startIndex}
                        onClose={() => setOpenPiece(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
